import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { toast } from 'react-toastify';
import jsPDF from 'jspdf';
import { FaDownload, FaEye, FaTimes } from 'react-icons/fa';
import * as API from "../Endpoints/Endpoints";

function ViewVcBot() {
  const [bot, setChatbots] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [viewOpen, setViewOpen] = useState(false);
  const [viewLoading, setViewLoading] = useState(false);
  const [selected, setSelected] = useState(null);
  const modalRef = useRef(null);

  useEffect(() => {
    const fetchbot = async () => {
      try {
        const response = await axios.get(API.GET_VALUECHAIN_API);
        setChatbots(response.data || []);
      } catch (error) {
        console.error('Error fetching valuechain data:', error);
        toast.error('Failed to fetch valuechain data');
      }
    };
    fetchbot();
  }, []);

  useEffect(() => {
    if (viewOpen && modalRef.current) {
      setTimeout(() => {
        try { modalRef.current.scrollTop = 0; } catch (e) {}
      }, 0);
    }
  }, [viewOpen]);

  const refreshList = async () => {
    try {
      const res = await axios.get(API.GET_VALUECHAIN_API);
      setChatbots(res.data || []);
    } catch (err) {
      console.error(err);
    }
  };

  const filteredBots = bot.filter((item) => {
    const q = (searchQuery || '').toLowerCase();
    return (
      item.useremail?.toLowerCase().includes(q) ||
      item.organizationname?.toLowerCase().includes(q) ||
      item.valuechaindescription?.toLowerCase().includes(q) ||
      item.valuechain?.toLowerCase().includes(q) ||
      item.subvaluechain?.toLowerCase().includes(q) ||
      item.valuechaintechnology?.toLowerCase().includes(q) ||
      item.valuechainsubtechnology?.toLowerCase().includes(q) ||
      item.valuechainrelatedterms?.toLowerCase().includes(q) ||
      item.taxonomy?.toLowerCase().includes(q)
    );
  });

  const handleView = async (item) => {
    try {
      setViewLoading(true);
      setViewOpen(true);
      setSelected(null);

      const res = await axios.get(API.GET_VALUECHAIN_API);
      const rec = (res.data || []).find((r) => r.valuechainid === item.valuechainid);
      if (!rec) {
        toast.error('Record not found');
        setViewOpen(false);
        return;
      }
      setSelected(rec);
    } catch (err) {
      console.error(err);
      toast.error('Failed to load details');
      setViewOpen(false);
    } finally {
      setViewLoading(false);
    }
  };

  const handleImport = (item) => {
    const doc = new jsPDF();
    let yPosition = 20;
    const lineHeight = 10;
    const maxWidth = 180;
    const pageHeight = doc.internal.pageSize.height;

    const safeText = (t) => String(t ?? '');

    const addText = (text, x, y) => {
      const splitText = doc.splitTextToSize(safeText(text), maxWidth);
      doc.text(splitText, x, y);
      return splitText.length * lineHeight;
    };

    const checkAndAddPage = (currentYPosition) => {
      if (currentYPosition + lineHeight > pageHeight) {
        doc.addPage();
        return 20;
      }
      return currentYPosition;
    };

    const addHeading = (heading, x, y) => {
      doc.setFont('helvetica', 'bold');
      y += addText(heading, x, y);
      doc.setFont('helvetica', 'normal');
      return y;
    };

    yPosition = addHeading(`Organization Name:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.organizationname, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`User Email:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.useremail, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`Valuechain:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.valuechain, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`Sub Valuechain:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.subvaluechain, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`Description:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.valuechaindescription, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`Technology:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.valuechaintechnology, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`Sub Technology:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.valuechainsubtechnology, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`Related Terms:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.valuechainrelatedterms, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    yPosition = addHeading(`Taxonomy:`, 10, yPosition);
    yPosition = checkAndAddPage(yPosition);
    yPosition += addText(item.taxonomy, 10, yPosition);
    yPosition = checkAndAddPage(yPosition + lineHeight);

    const filename = (item.valuechain || 'valuechain').replace(/\s+/g, '_');
    doc.save(`${filename}.pdf`);
  };

  return (
    <div className="container mx-auto px-4 mt-10">
      <div className="flex justify-between items-center mb-6">
        <h1 className="text-3xl font-bold">Valuechain References</h1>
        <input
          type="text"
          placeholder="Search..."
          className="px-4 py-2 border rounded-md"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full bg-white border-gray-300 shadow-md rounded-md table-fixed">
          <colgroup>
            <col style={{ width: '7%' }} />
            <col style={{ width: '4%' }} />
            <col />
          </colgroup>

          <thead className="bg-gray-800 text-white">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-semibold">Actions</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">Sr No</th>
              <th className="px-4 py-3 text-left text-sm font-semibold">Valuechain / Description</th>
            </tr>
          </thead>

          <tbody>
            {filteredBots.length > 0 ? (
              filteredBots.map((item, index) => (
                <tr key={item.valuechainid || index} className="border-b hover:bg-gray-50">
                  <td className="px-4 py-3 whitespace-normal">
                    <div className="flex items-center gap-2">
                      <button
                        className="text-indigo-600 hover:underline flex items-center gap-1"
                        onClick={() => handleView(item)}
                        title="View"
                      >
                        <FaEye />
                      </button>

                      <button
                        className="text-blue-500 hover:underline flex items-center gap-1"
                        onClick={() => handleImport(item)}
                        title="Export as PDF"
                      >
                        <FaDownload />
                      </button>

                      {/* Uncomment and wire your edit/delete components:
                          <EdituserVC valuechainid={item.valuechainid} refreshList={refreshList} />
                          <DeleteuserVC valuechainid={item.valuechainid} refreshList={refreshList} />
                      */}
                    </div>
                  </td>

                  <td className="px-4 py-3 whitespace-normal text-sm">{index + 1}</td>

                  <td className="px-4 py-3 whitespace-normal text-sm break-words max-w-[640px]">
                    {/* <div className="font-semibold">{item.valuechain || '—'}</div> */}
                    <div className="text-sm text-gray-700 mt-1">{item.valuechaindescription}</div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="3" className="px-6 py-8 text-center text-gray-500">
                  No Valuechain References found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* VIEW MODAL */}
      {viewOpen && (
        <div className="fixed inset-0 z-50 flex items-start sm:items-center justify-center bg-black/50 p-4">
          <div
            ref={modalRef}
            className="bg-white w-full max-w-4xl rounded-2xl shadow-xl p-6 relative overflow-y-auto max-h-[90vh]"
          >
            <button
              className="absolute top-4 right-4 text-gray-600 hover:text-black"
              onClick={() => { setViewOpen(false); setSelected(null); }}
              aria-label="Close"
            >
              <FaTimes />
            </button>

            {viewLoading ? (
              <div className="py-20 text-center">Loading...</div>
            ) : selected ? (
              <>
                <div className="mb-2 text-2xl font-semibold break-words">
                  {selected.valuechain || 'Untitled Valuechain'}
                </div>
                <div className="text-sm text-gray-600 mb-4">
                  {selected.useremail ? `By ${selected.useremail}` : ''} {selected.organizationname ? `• ${selected.organizationname}` : ''}
                </div>

                <PdfSection title="Description" text={selected.valuechaindescription} />
                <PdfSection title="Sub Valuechain" text={selected.subvaluechain} />
                <PdfSection title="Technology" text={selected.valuechaintechnology} />
                <PdfSection title="Sub Technology" text={selected.valuechainsubtechnology} />
                <PdfSection title="Related Terms" text={selected.valuechainrelatedterms} />
                <PdfSection title="Taxonomy" text={selected.taxonomy} />

                <div className="mt-6 flex justify-end">
                  <button
                    className="px-6 py-2 bg-gray-600 text-white rounded-lg shadow hover:bg-gray-700"
                    onClick={() => { setViewOpen(false); setSelected(null); }}
                  >
                    Back
                  </button>
                </div>
              </>
            ) : (
              <div className="py-20 text-center text-red-600">No record found.</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function PdfSection({ title, text }) {
  if (!text) return null;
  return (
    <div className="mt-4">
      <h3 className="text-lg font-semibold border-b border-gray-200 pb-1">{title}</h3>
      <div className="mt-2 whitespace-pre-wrap leading-relaxed text-gray-800 break-words">
        {text}
      </div>
    </div>
  );
}

export default ViewVcBot;
